// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MqttActivityProvider, useMqttActivity } from '../context/MqttActivityContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { mergeBatchClientEntry } from '../utils/batchClientMerge';
import { fmtDateTime } from '../utils/timezoneUtils';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';
import { keyframes } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RouterIcon from '@mui/icons-material/Router';
import DevicesIcon from '@mui/icons-material/Devices';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneIcon from '@mui/icons-material/PhoneAndroid';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import LanIcon from '@mui/icons-material/Lan';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import QuestionIcon from '@mui/icons-material/QuestionMark';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  isDeviceOnline,
  getHealthIndicatorClass,
  getHealthLabel,
  normalizeHealth,
} from '../utils/healthStatus';
import { createCancelableRemoteGet } from '../utils/remoteApiCancelable';
import HeartbeatECG from '../components/HeartbeatECG';

// StargateVersionInfo - shows the device's OS (firmware) and UI versions on the tile.
// Fetches per-device from the same remote endpoints the detail page uses. Only probes
// healthy (reachable) devices; degraded/offline devices show a status placeholder.
const StargateVersionInfo = ({ ipAddress, apiKey, healthStatus }) => {
  const [osVersion, setOsVersion] = useState(null);
  const [uiVersion, setUiVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestsRef = useRef([]);

  useEffect(() => {
    // Cancel any in-flight requests from a previous render
    requestsRef.current.forEach((r) => r?.cancel?.());
    requestsRef.current = [];

    if (!ipAddress || !apiKey) {
      setOsVersion('N/A');
      setUiVersion('N/A');
      return undefined;
    }

    // Don't probe unreachable devices — mirror the detail page's behaviour.
    const health = normalizeHealth(healthStatus);
    if (health !== 'green') {
      const placeholder = health === 'yellow' ? 'Degraded' : 'Offline';
      setOsVersion(placeholder);
      setUiVersion(placeholder);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const firmwareReq = createCancelableRemoteGet(ipAddress, apiKey, '/api/firmware/version');
    const uiReq = createCancelableRemoteGet(ipAddress, apiKey, '/api/system/ui-version');
    requestsRef.current = [firmwareReq, uiReq];

    Promise.allSettled([firmwareReq.request, uiReq.request]).then(([fw, ui]) => {
      if (cancelled) return;
      if (fw.status === 'fulfilled') {
        const d = fw.value;
        setOsVersion(d?.current_version || d?.version || d?.firmware_version || 'N/A');
      } else {
        setOsVersion('N/A');
      }
      setUiVersion(ui.status === 'fulfilled' ? ui.value?.version || 'N/A' : 'N/A');
      setLoading(false);
    });

    return () => {
      cancelled = true;
      requestsRef.current.forEach((r) => r?.cancel?.());
      requestsRef.current = [];
    };
  }, [ipAddress, apiKey, healthStatus]);

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        OS Version: {loading ? '…' : osVersion || 'N/A'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        UI Version: {loading ? '…' : uiVersion || 'N/A'}
      </Typography>
    </>
  );
};

// ClientBadge component - shows client count with color-coded states
// Defined outside StargatesPageInner to prevent recreation on every render
const ClientBadge = ({ stargateId, isStargateOnline, clientData, openPopoversRef, isFetching }) => {
  const data = clientData[stargateId] || null;
  const [anchorEl, setAnchorEl] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const anchorElRef = useRef(null);

  // Track popover open state to prevent refreshes while open
  useEffect(() => {
    if (anchorEl) {
      openPopoversRef.current.add(stargateId);
    } else {
      openPopoversRef.current.delete(stargateId);
    }
    return () => {
      openPopoversRef.current.delete(stargateId);
    };
  }, [anchorEl, stargateId, openPopoversRef]);

  // Badge/popover only list clients that have a real VPN (WG) IP.
  const hasVpnIp = (c) => !!(c.vpn_ip && String(c.vpn_ip).trim());

  const allEth1Clients =
    data && data.clients
      ? data.clients.filter((client) => {
          const iface = (client.interface || client.iface || '').toLowerCase();
          return iface === 'eth1' && hasVpnIp(client);
        })
      : [];
  const onlineEth1Clients = allEth1Clients.filter((c) => c.online !== false);

  // VLAN tunnel clients (same VPN-IP requirement for the badge)
  const allVlanClients =
    data && data.clients
      ? data.clients.filter((client) => {
          return client.source === 'vlan_tunnel' && hasVpnIp(client);
        })
      : [];

  const onlineVlanClients = allVlanClients.filter((c) => c.online === true);
  const totalClientCount = allEth1Clients.length + allVlanClients.length;
  const eth1Count = allEth1Clients.length;
  const onlineEth1Count = onlineEth1Clients.length;
  const vlanCount = allVlanClients.length;
  const onlineVlanCount = onlineVlanClients.length;

  // Determine badge state and color
  // Only show loading if we have no cached data
  const hasData = data && (data.clients !== undefined || data.error !== undefined);

  let badgeState = 'loading'; // loading, error (orange), zero (red), active (green)
  let badgeColor = '#9e9e9e'; // gray for loading
  let badgeText = '...';

  if (!isStargateOnline) {
    badgeState = 'offline';
    badgeColor = '#9e9e9e';
    badgeText = 'N/A';
  } else if (hasData) {
    const hasSuccessfulData = data.clients && data.clients.length > 0 && !data.error;

    if (data.error && !hasSuccessfulData) {
      badgeState = 'error';
      badgeColor = '#ff9800'; // orange
      badgeText = '!';
    } else if (totalClientCount === 0) {
      badgeState = 'zero';
      badgeColor = '#f44336'; // red
      badgeText = '0';
    } else {
      badgeState = 'active';
      const anyOnline = onlineEth1Count > 0 || onlineVlanCount > 0;
      badgeColor = anyOnline ? '#4caf50' : '#ff9800';
      badgeText = totalClientCount.toString();
    }
  }
  // If no data (neither fresh nor cached), show loading state (...)

  const handleMouseEnter = (event) => {
    event.stopPropagation();
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Store element reference immediately
    const element = event.currentTarget;
    anchorElRef.current = element;
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorEl(element);
    }, 300);
  };

  const handleMouseLeave = (event) => {
    event.stopPropagation();
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay closing to allow moving mouse to popover
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const handlePopoverMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Tooltip content with client information
  const tooltipContent = (
    <Box sx={{ maxWidth: 360, minWidth: 280 }}>
      {isFetching && data && !data.error && (
        <LinearProgress
          sx={{
            height: 2,
            borderRadius: '10px 10px 0 0',
            backgroundColor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' },
          }}
        />
      )}
      <Box sx={{ p: 1.5 }}>
        {!isStargateOnline ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WifiOffIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
            <Typography variant="body2">Stargate offline - client data unavailable</Typography>
          </Box>
        ) : !data ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              py: 2,
            }}
          >
            <CircularProgress size={24} sx={{ color: '#4caf50' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Loading client data...
            </Typography>
          </Box>
        ) : data.error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Connection Error
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Unable to reach Stargate endpoint
              </Typography>
            </Box>
          </Box>
        ) : totalClientCount === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WifiOffIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
            <Typography variant="body2">No downstream clients connected</Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1.5,
                mb: 1.5,
                borderBottom: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <WifiIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                {totalClientCount} Client{totalClientCount !== 1 ? 's' : ''}
                {eth1Count > 0 && vlanCount > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.5)', ml: 0.5 }}
                  >
                    ({eth1Count} eth + {vlanCount} vlan)
                  </Typography>
                )}
              </Typography>
            </Box>

            {/* Eth1 client list */}
            {allEth1Clients && allEth1Clients.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    display: 'block',
                    mb: 1,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '10px',
                  }}
                >
                  Devices (eth1)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {allEth1Clients.slice(0, 6).map((client, idx) => {
                    const displayName =
                      client.hostname && client.hostname !== 'unknown'
                        ? client.hostname
                        : client.ip || 'Unknown device';
                    const isClientOnline = client.online !== false;
                    return (
                      <Box
                        key={`eth-${idx}`}
                        sx={{
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          opacity: isClientOnline ? 1 : 0.6,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              backgroundColor: isClientOnline ? '#4caf50' : '#9e9e9e',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '12px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {displayName}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            gap: '4px 12px',
                            fontSize: '10px',
                          }}
                        >
                          {client.ip && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                IP:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.85)',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {client.ip}
                              </Typography>
                            </>
                          )}
                          {client.mac && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                MAC:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.85)',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {client.mac}
                              </Typography>
                            </>
                          )}
                          {client.vpn_ip && String(client.vpn_ip).trim() !== '' ? (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                VPN:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#81c784',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {String(client.vpn_ip).trim()}
                              </Typography>
                            </>
                          ) : null}
                          {client.interface && client.interface !== 'unknown' && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                Interface:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.7)',
                                  fontSize: '10px',
                                }}
                              >
                                {client.interface}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {allEth1Clients.length > 6 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1.5,
                      color: 'rgba(255,255,255,0.5)',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    +{allEth1Clients.length - 6} more eth device
                    {allEth1Clients.length - 6 !== 1 ? 's' : ''}...
                  </Typography>
                )}
              </Box>
            )}

            {/* VLAN tunnel client list */}
            {allVlanClients && allVlanClients.length > 0 && (
              <Box sx={{ mt: allEth1Clients.length > 0 ? 2 : 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    display: 'block',
                    mb: 1,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '10px',
                  }}
                >
                  <LanIcon sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
                  VLAN Tunnels ({vlanCount})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {allVlanClients.slice(0, 6).map((client, idx) => {
                    const displayName =
                      client.hostname && client.hostname !== 'unknown'
                        ? client.hostname
                        : client.ip || 'Unknown device';
                    const isVlanClientOnline = client.online === true;
                    return (
                      <Box
                        key={`vlan-${idx}`}
                        sx={{
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(156, 39, 176, 0.08)',
                          border: '1px solid rgba(156, 39, 176, 0.2)',
                          opacity: isVlanClientOnline ? 1 : 0.6,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              backgroundColor: isVlanClientOnline ? '#4caf50' : '#f44336',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '12px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {displayName}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            gap: '4px 12px',
                            fontSize: '10px',
                          }}
                        >
                          {client.ip && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                IP:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.85)',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {client.ip}
                              </Typography>
                            </>
                          )}
                          {client.mac && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                MAC:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.85)',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {client.mac}
                              </Typography>
                            </>
                          )}
                          {client.vpn_ip && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                VPN:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#81c784',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                }}
                              >
                                {client.vpn_ip}
                              </Typography>
                            </>
                          )}
                          {client.vlan_id && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  fontSize: '10px',
                                }}
                              >
                                VLAN:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#ce93d8', fontSize: '10px' }}
                              >
                                {client.vlan_id}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {allVlanClients.length > 6 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1.5,
                      color: 'rgba(255,255,255,0.5)',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    +{allVlanClients.length - 6} more VLAN device
                    {allVlanClients.length - 6 !== 1 ? 's' : ''}...
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10000,
          borderRadius: '12px',
          padding: '6px 12px',
          minWidth: '36px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          backgroundColor: badgeColor,
          color: 'text.primary',
          fontWeight: 700,
          fontSize: '13px',
          lineHeight: 1,
          transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.3s',
          pointerEvents: 'auto !important',
          ...(badgeState === 'loading' && {
            animation: `${keyframes`0%,100%{opacity:1}50%{opacity:0.5}`} 1.5s ease-in-out infinite`,
          }),
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {badgeState === 'loading' ? (
          <CircularProgress size={12} sx={{ color: '#fff' }} />
        ) : isFetching ? (
          <>
            <CircularProgress size={10} thickness={5} sx={{ color: 'rgba(255,255,255,0.7)' }} />
            {badgeText}
          </>
        ) : (
          badgeText
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            pointerEvents: 'auto',
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(20, 20, 25, 0.98)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            maxWidth: 380,
            p: 0,
            mt: 1,
            ml: 1,
          },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
        }}
        disableRestoreFocus
      >
        {tooltipContent}
      </Popover>
    </>
  );
};

function StargatesPageInner() {
  const { isDarkMode } = useTheme();
  // Snackbar for feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  // MQTT activity context for status (activityMap drives the tile ECG beat:
  // it is stamped on every live MQTT message from a device)
  const {
    getStargateStatus,
    getStargateStatusByIp,
    stargateStatusMap,
    activityMap,
    tick,
    setStargateList,
  } = useMqttActivity();

  // Use tick in a useEffect to force refresh of status indicators
  useEffect(() => {
    // This empty effect ensures component rerenders when tick changes
    // The tick value changes every 5 seconds from MqttActivityContext
  }, [tick]);

  const { regionId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [region, setRegion] = useState(null);
  const [stargates, setStargates] = useState([]);
  const [edgeDevices, setEdgeDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duplicateFieldDialog, setDuplicateFieldDialog] = useState({
    open: false,
    field: '',
    value: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStargate, setEditingStargate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stargateData, setStargateData] = useState({
    friendly_name: '',
    model: '',
    serial_number: '',
    ip_address: '',
    api_key: '',
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stargateToDelete, setStargateToDelete] = useState(null);
  const [nextStargateNumber, setNextStargateNumber] = useState(1);
  const [clientData, setClientData] = useState(() => {
    sessionStorage.removeItem('stargateClientData');
    return {};
  });
  const clientDataRef = useRef(clientData);
  clientDataRef.current = clientData;
  const [fetchingSet, setFetchingSet] = useState(new Set());
  const openPopoversRef = useRef(new Set());
  const hasFetchedRef = useRef(new Set());
  const clientDataInFlight = useRef(false);

  // Process raw batch response for a single Stargate into the clientData format
  const processStargateClients = useCallback((stargate, ethData, vlanData) => {
    let ethClients = [];
    if (ethData) {
      const d = ethData;
      if (d.all_clients && Array.isArray(d.all_clients)) ethClients = d.all_clients;
      else if (Array.isArray(d)) ethClients = d;
      else if (d.clients && Array.isArray(d.clients)) ethClients = d.clients;
      else if (d.data && Array.isArray(d.data)) ethClients = d.data;
    }

    const existingClientsByMac = new Map();
    const prev = clientDataRef.current[stargate.id];
    if (prev && prev.clients) {
      prev.clients.forEach((c) => {
        if (c.mac) existingClientsByMac.set(c.mac.toUpperCase(), c);
      });
    }

    const stargateIP = stargate.ip_address;
    const processedEthClients = ethClients
      .filter((client) => {
        const ip = client.ip_address || client.ip || '';
        if (ip === stargateIP) return false;
        const deviceType = client.device_info?.device_type || '';
        if (deviceType.toLowerCase() === 'gateway' || deviceType.toLowerCase() === 'router')
          return false;
        if (
          client.client_type === 'gateway' ||
          client.type === 'gateway' ||
          client.type === 'router' ||
          client.is_gateway
        )
          return false;
        const isPrivate =
          ip.startsWith('10.') ||
          ip.startsWith('192.168.') ||
          (ip.startsWith('172.') &&
            parseInt(ip.split('.')[1]) >= 16 &&
            parseInt(ip.split('.')[1]) <= 31);
        return isPrivate || !!client.vpn_ip;
      })
      .map((client) => {
        const mac = (client.mac_address || client.mac || '').toUpperCase();
        const existingClient = mac ? existingClientsByMac.get(mac) : null;
        const vpnIpFromPayload = client.vpn_ip || null;
        const preservedVpnIp =
          vpnIpFromPayload || (existingClient && existingClient.vpn_ip) || null;
        const st = (client.status || '').toLowerCase();
        const src = (client.source || client.client_type || '').toLowerCase();
        const isDhcpLease = src.includes('dhcp');
        const isOnline =
          isDhcpLease ||
          client.online === true ||
          st === 'online' ||
          st === 'active' ||
          st === 'connected' ||
          (!st && client.online !== false);
        return {
          ip: client.ip_address || client.ip || '',
          mac,
          hostname: client.hostname || (existingClient && existingClient.hostname) || 'unknown',
          vendor:
            client.device_info?.vendor ||
            client.vendor ||
            (existingClient && existingClient.vendor) ||
            '',
          vpn_ip: preservedVpnIp,
          interface: 'eth1',
          online: isOnline,
          source: 'ethernet',
        };
      })
      // Card badge is VPN-addressed clients only.
      .filter((c) => !!(c.vpn_ip && String(c.vpn_ip).trim()));

    let processedVlanClients = [];
    if (vlanData) {
      const vlanClients = Array.isArray(vlanData.clients) ? vlanData.clients : [];
      processedVlanClients = vlanClients
        .map((client) => ({
          ip: client.ip || '',
          mac: (client.mac || '').toUpperCase(),
          hostname: client.hostname || 'unknown',
          vendor: '',
          vpn_ip: client.vpn_ip || null,
          interface: `vlan${client.vlan_id || ''}`,
          online: client.online === true || client.online !== false,
          source: 'vlan_tunnel',
          vlan_id: client.vlan_id,
          bridge: client.bridge,
          wg_namespace: client.wg_namespace,
          device_type: client.device_type,
        }))
        .filter((c) => !!(c.vpn_ip && String(c.vpn_ip).trim()));
    }

    const seenMacs = new Set();
    const allClients = [];
    for (const c of [...processedEthClients, ...processedVlanClients]) {
      if (c.mac && seenMacs.has(c.mac)) continue;
      if (c.mac) seenMacs.add(c.mac);
      allClients.push(c);
    }

    return {
      online: allClients.filter((c) => c.online).length,
      clients: allClients,
      error: false,
      lastUpdated: new Date().toISOString(),
    };
  }, []);

  // Single batch call to fetch client data for all stargates at once.
  // Skip offline/red devices — one unreachable peer otherwise stalls the whole
  // batch past the SPA timeout and paints orange "!" on every online card.
  const fetchAllClientData = useCallback(
    async (stargatesList) => {
      const targets = stargatesList.filter(
        (sg) =>
          sg.ip_address &&
          sg.api_key &&
          isDeviceOnline(sg.health_status) &&
          !openPopoversRef.current.has(sg.id)
      );
      if (!targets.length) return;
      if (clientDataInFlight.current) return;
      clientDataInFlight.current = true;

      setFetchingSet(new Set(targets.map((sg) => sg.id)));

      try {
        const resp = await axios.post(
          '/proxy/network/batch-clients',
          {
            stargate_ids: targets.map((sg) => sg.id),
            interface: 'eth1',
          },
          { timeout: 45000, _skipRetry: true }
        );

        const batch = resp.data || {};

        setClientData((prev) => {
          const next = { ...prev };
          for (const sg of targets) {
            const raw = batch[String(sg.id)];
            const success =
              raw && !raw.error
                ? processStargateClients(sg, raw.ethernet, raw.vlan)
                : null;
            next[sg.id] = mergeBatchClientEntry(
              prev[sg.id],
              success,
              raw?.message || 'batch error'
            );
            hasFetchedRef.current.add(sg.id);
          }
          return next;
        });
      } catch (err) {
        setClientData((prev) => {
          const next = { ...prev };
          for (const sg of targets) {
            next[sg.id] = mergeBatchClientEntry(
              prev[sg.id],
              null,
              err?.message || 'batch error'
            );
            hasFetchedRef.current.add(sg.id);
          }
          return next;
        });
      } finally {
        clientDataInFlight.current = false;
        setFetchingSet(new Set());
      }
    },
    [processStargateClients]
  );

  // Track whether we've done the initial load (to distinguish first visit vs revisit)
  const hasLoadedRef = useRef(false);

  // Fetch region and stargates
  const fetchData = useCallback(
    async (isBackgroundRefresh = false) => {
      // Only show loading spinner on first load, not on background refreshes
      if (!isBackgroundRefresh && !hasLoadedRef.current) {
        setLoading(true);
      }
      try {
        // Fetch region details, stargates AND edge devices in parallel. Edge devices
        // are non-critical, so a failure there must not break the page.
        const [regionResponse, stargatesResponse, edgeResponse] = await Promise.all([
          axios.get(`regions/${regionId}`),
          axios.get(`stargates?region_id=${regionId}`),
          axios.get(`edge-devices?region_id=${regionId}`).catch(() => ({ data: [] })),
        ]);
        setRegion(regionResponse.data);
        setEdgeDevices(Array.isArray(edgeResponse.data) ? edgeResponse.data : []);
        const sorted = [...stargatesResponse.data].sort(
          (a, b) =>
            (a.friendly_name || '').localeCompare(b.friendly_name || '', undefined, {
              numeric: true,
            }) || a.id - b.id
        );
        setStargates(sorted);

        // Share the stargate list with MQTT context so it can correlate activity data
        if (setStargateList) {
          setStargateList(stargatesResponse.data);
        }

        fetchAllClientData(stargatesResponse.data);

        // Determine next stargate number for default naming
        const existingNumbers = stargatesResponse.data
          .map((sg) => {
            const match = sg.friendly_name.match(/Stargate (\d+)/);
            return match ? parseInt(match[1], 10) : 0;
          })
          .filter((num) => !isNaN(num));

        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

        setNextStargateNumber(nextNumber);

        setError('');
        hasLoadedRef.current = true;
      } catch (err) {
        // Only show error if we have no data yet
        if (!hasLoadedRef.current) {
          setError('Failed to load data. Please try again later.');
        }
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    },
    [regionId, setStargateList, fetchAllClientData]
  );

  useEffect(() => {
    // Reset on region change
    hasLoadedRef.current = false;
    fetchData(false);
  }, [regionId, fetchData]);

  // Set up periodic refresh to keep data in sync (background, no spinner)
  useEffect(() => {
    // Refresh data every 30 seconds in the background
    const refreshInterval = setInterval(() => {
      fetchData(true); // background refresh - no loading spinner
    }, 30000);

    // Refresh client data every 60 seconds (silently, using cached data)
    const clientRefreshInterval = setInterval(() => {
      if (stargates.length > 0) {
        fetchAllClientData(stargates, true); // silent refresh
      }
    }, 60000);

    // Cleanup intervals on unmount
    return () => {
      clearInterval(refreshInterval);
      clearInterval(clientRefreshInterval);
    };
  }, [fetchData, fetchAllClientData, stargates]);

  // Handle stargate click
  const handleStargateClick = (stargateId) => {
    navigate(`/stargates/${stargateId}`);
  };

  // Open dialog for adding/editing stargate
  const handleOpenDialog = (stargate = null) => {
    if (stargate) {
      setEditingStargate(stargate);
      setStargateData({
        friendly_name: stargate.friendly_name,
        model: stargate.model,
        serial_number: stargate.serial_number,
        ip_address: stargate.ip_address || '',
        api_key: stargate.api_key || '',
      });
    } else {
      setEditingStargate(null);
      setStargateData({
        friendly_name: `Stargate ${nextStargateNumber}`,
        model: 'SG-1000',
        serial_number: '',
        ip_address: '',
        api_key: '',
      });
    }
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStargate(null);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStargateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Save stargate
  const handleSaveStargate = async () => {
    // Require all fields filled
    if (
      !stargateData.friendly_name.trim() ||
      !stargateData.model.trim() ||
      !stargateData.serial_number.trim() ||
      !stargateData.ip_address.trim() ||
      !stargateData.api_key.trim()
    ) {
      setError('All fields are required, including the API Key.');
      return;
    }
    setSaving(true);
    try {
      if (editingStargate) {
        // Update existing stargate
        await axios.put(`stargates/${editingStargate.id}`, {
          ...stargateData,
        });
      } else {
        // Create new stargate (backend enforces uniqueness for name, serial, IP, api_key)
        const payload = {
          ...stargateData,
          region_id: parseInt(regionId, 10),
        };

        // Debug logging for Stargate creation
        console.log('=====================================');
        console.log('FRONTEND: Creating new Stargate');
        console.log('=====================================');
        console.log('Request Payload:', payload);
        console.log('  - Friendly Name:', payload.friendly_name);
        console.log('  - Model:', payload.model);
        console.log('  - Serial Number:', payload.serial_number);
        console.log('  - IP Address:', payload.ip_address);
        console.log(
          '  - API Key:',
          payload.api_key ? `***${payload.api_key.slice(-4)}` : 'NOT PROVIDED'
        );
        console.log('  - Region ID:', payload.region_id);
        console.log('=====================================');

        const createResponse = await axios.post('stargates', payload);

        // After creation, just close dialog and refresh
        const newStargate = createResponse.data;

        console.log('FRONTEND: Stargate creation response received');
        console.log('Response Data:', newStargate);
        console.log('  - ID:', newStargate.id);
        console.log('  - Serial Number:', newStargate.serial_number);
        console.log('  - IP Address:', newStargate.ip_address);
        console.log('  - MQTT Username:', newStargate.mqtt_username);
        console.log('=====================================');
        console.log('MQTT Configuration will be uploaded to:');
        console.log(`  http://${newStargate.ip_address}/api/mqtt/run-script`);
        console.log('=====================================\n');

        // Close dialog and refresh data immediately
        handleCloseDialog();
        fetchData();

        // Show success message for Stargate creation
        setSnackbar({
          open: true,
          message: `Stargate "${newStargate.friendly_name}" created successfully. MQTT configuration is being uploaded to the device.`,
          severity: 'success',
        });

        // Exit early for new stargate creation
        setSaving(false);
        return;
      }

      // For editing existing stargate
      handleCloseDialog();
      fetchData();
    } catch (err) {
      // Enhanced error logging
      console.error('=====================================');
      console.error('FRONTEND: Error creating/updating Stargate');
      console.error('=====================================');
      console.error('Error Details:', err);
      console.error('Response Status:', err?.response?.status);
      console.error('Response Data:', err?.response?.data);
      console.error('Error Message:', err?.message);
      console.error('=====================================\n');

      // Handle 504 Gateway Timeout specially - backend may still be processing
      if (err?.response?.status === 504 || err?.response?.status === 503) {
        console.log('Gateway timeout detected, checking if Stargate was created...');

        // For new Stargate creation, check if it was actually created
        if (!editingStargate) {
          // Poll to check if the Stargate was created
          let found = false;
          const maxAttempts = 10;
          const pollInterval = 2000; // 2 seconds

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
              const allStargatesResp = await axios.get(`stargates?region_id=${regionId}`);
              const createdStargate = allStargatesResp.data.find(
                (sg) =>
                  sg.serial_number === stargateData.serial_number ||
                  (sg.friendly_name === stargateData.friendly_name &&
                    sg.ip_address === stargateData.ip_address)
              );

              if (createdStargate) {
                found = true;
                console.log('Stargate was successfully created despite timeout:', createdStargate);

                // Close dialog and refresh data
                handleCloseDialog();
                fetchData();

                // Show success message
                setSnackbar({
                  open: true,
                  message: `Stargate "${createdStargate.friendly_name}" created successfully. The operation took longer than expected, but it completed successfully.`,
                  severity: 'success',
                });
                setSaving(false);
                return;
              }
            } catch (pollErr) {
              console.warn(`Poll attempt ${attempt} failed:`, pollErr);
            }
          }

          // If we didn't find it after polling, show a warning but don't treat as error
          if (!found) {
            console.warn(
              'Stargate not found after polling, but operation may still be in progress'
            );
            setSnackbar({
              open: true,
              message:
                'The operation is taking longer than expected. The Stargate may still be created in the background. Please refresh the page in a few moments.',
              severity: 'warning',
            });
            handleCloseDialog();
            fetchData(); // Refresh anyway to show if it appears
            setSaving(false);
            return;
          }
        } else {
          // For updates, just show a warning
          setSnackbar({
            open: true,
            message:
              'The update operation is taking longer than expected. Please refresh the page in a few moments to verify the changes.',
            severity: 'warning',
          });
          handleCloseDialog();
          fetchData();
          setSaving(false);
          return;
        }
      }

      // Detect duplicate field error (serial, name, or ip)
      const backendMsg = err?.response?.data?.detail || err?.response?.data || '';
      if (
        (err?.response?.status === 400 || err?.response?.status === 409) &&
        typeof backendMsg === 'string'
      ) {
        let field = '';
        let value = '';
        const lowerMsg = backendMsg.toLowerCase();
        if (lowerMsg.includes('duplicate serial') || lowerMsg.includes('serial number')) {
          field = 'Serial Number';
        } else if (lowerMsg.includes('friendly_name') || lowerMsg.includes('name')) {
          field = 'Name';
        } else if (lowerMsg.includes('ip_address') || lowerMsg.includes('ip address')) {
          field = 'IP Address';
        }
        // Attempt to extract value from message
        const match = backendMsg.match(/already exists: (.+)$/);
        if (match) value = match[1];
        if (field) {
          setDuplicateFieldDialog({ open: true, field, value });
        } else {
          setError(backendMsg || 'Failed to save stargate. Please try again.');
        }
      } else {
        setError('Failed to save stargate. Please try again.');
      }
      console.error('Error saving stargate:', err);
    }
    setSaving(false);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (stargate, event) => {
    event.stopPropagation();
    setStargateToDelete(stargate);
    setDeleteConfirmOpen(true);
  };

  // Delete stargate
  const handleDeleteStargate = async () => {
    setDeleting(true);
    const deletedId = stargateToDelete?.id;
    try {
      await axios.delete(`stargates/${deletedId}`);
      setDeleteConfirmOpen(false);
      setStargateToDelete(null);

      // Purge client data and session cache for the deleted stargate
      if (deletedId != null) {
        setClientData((prev) => {
          const next = { ...prev };
          delete next[deletedId];
          return next;
        });
        hasFetchedRef.current.delete(deletedId);
      }

      fetchData();
      setSnackbar({
        open: true,
        message:
          'Stargate deleted. MQTT credentials have been revoked and the device can no longer connect.',
        severity: 'success',
      });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setSnackbar({
          open: true,
          message: 'This stargate no longer exists in the system. Refreshing the list...',
          severity: 'warning',
        });
        setDeleteConfirmOpen(false);
        setStargateToDelete(null);
        fetchData();
      } else {
        setSnackbar({
          open: true,
          message: `Failed to delete stargate: ${err?.response?.data?.detail || 'Please try again.'}`,
          severity: 'error',
        });
      }
      console.error('Error deleting stargate:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Push current MQTT config to a remote Stargate device
  const [resyncingId, setResyncingId] = useState(null);
  const [resyncConfirmOpen, setResyncConfirmOpen] = useState(false);
  const [stargateToResync, setStargateToResync] = useState(null);

  const handleResyncClick = (stargate, e) => {
    e.stopPropagation();
    setStargateToResync(stargate);
    setResyncConfirmOpen(true);
  };

  const handleResyncMqtt = async () => {
    const stargate = stargateToResync;
    if (!stargate) return;
    setResyncConfirmOpen(false);
    setResyncingId(stargate.id);
    try {
      const res = await axios.post(`stargates/${stargate.id}/push-mqtt-config`, null, {
        timeout: 15000,
      });
      // Immediately show the tile as degraded since the device's MQTT client is restarting
      setStargates((prev) =>
        prev.map((sg) =>
          sg.id === stargate.id
            ? {
                ...sg,
                health_status: 'yellow',
                connection_status: 'mqtt_restarting',
              }
            : sg
        )
      );
      setSnackbar({
        open: true,
        message: res.data?.message || `MQTT config push queued for ${stargate.friendly_name}`,
        severity: 'success',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const respStatus = err?.response?.status;
      let msg;
      if (detail) {
        msg = detail;
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = `Request timed out — the server may be unreachable`;
      } else {
        msg = err.message;
      }
      setSnackbar({
        open: true,
        message: `MQTT resync failed${respStatus ? ` (${respStatus})` : ''}: ${msg}`,
        severity: 'error',
      });
    } finally {
      setResyncingId(null);
      setStargateToResync(null);
    }
  };

  const formatTimeAgo = useCallback((isoString) => {
    if (!isoString) return 'never';
    const then = new Date(isoString);
    const now = new Date();
    const diffMs = now - then;
    if (diffMs < 0) return 'just now';
    const secs = Math.floor(diffMs / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ${hrs % 24}h ago`;
  }, []);

  const renderHealthIndicator = (stargate) => {
    const label = getHealthLabel(stargate.health_status);
    const lastSeen = stargate.last_seen;
    const connStatus = stargate.connection_status || 'unknown';

    const localTime = lastSeen ? fmtDateTime(lastSeen) : 'No data';
    const ago = formatTimeAgo(lastSeen);

    const tooltipContent = (
      <Box sx={{ p: 0.5, lineHeight: 1.6 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
          {label} — {connStatus}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Last MQTT: {localTime}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
          ({ago})
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <span
          className={`health-indicator ${getHealthIndicatorClass(stargate.health_status)}`}
        ></span>
      </Tooltip>
    );
  };

  // Only show edge devices that are NOT Stargates. An edge probe whose WG IP
  // matches a Stargate in this region duplicates the Stargate card shown above,
  // so hide it here (it still lives on the dedicated Edge Latency page).
  const visibleEdgeDevices = (() => {
    const sgIps = new Set(
      (stargates || []).map((s) => (s.ip_address || '').trim()).filter(Boolean)
    );
    return edgeDevices.filter(
      (d) => !((d.wg_ip || '').trim() && sgIps.has((d.wg_ip || '').trim()))
    );
  })();

  // Render flag emoji from country code
  const renderFlag = (countryCode) => {
    // Check if countryCode is a string and has valid length
    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      console.warn('Invalid country code format:', countryCode);
      return null;
    }

    // Convert country code to flag emoji
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            to="/"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
            Geographical Regions
          </Link>
          <Typography color="text.primary">
            {region?.name} {region?.country_flag && renderFlag(region.country_flag)}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            minWidth: 0,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1.35rem', sm: undefined },
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            Stargates in {region?.name}
          </Typography>
          {isAdmin() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
            >
              Add Stargate
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {stargates.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">
                  No stargates found in this region.{' '}
                  {isAdmin() && 'Click "Add Stargate" to create one.'}
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {stargates.map((stargate) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={stargate.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'transform 0.2s',
                        overflow: 'visible',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      {/* Client badge in top right corner */}
                      <ClientBadge
                        stargateId={stargate.id}
                        isStargateOnline={isDeviceOnline(stargate.health_status)}
                        clientData={clientData}
                        openPopoversRef={openPopoversRef}
                        isFetching={fetchingSet.has(stargate.id)}
                      />
                      <CardActionArea
                        onClick={() => handleStargateClick(stargate.id)}
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '60px',
                            height: '60px',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            pt: 2.5,
                            pr: 2.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <RouterIcon
                              sx={{
                                fontSize: 40,
                                mr: 1,
                                color: 'primary.main',
                              }}
                            />
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {stargate.friendly_name}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}
                          >
                            {renderHealthIndicator(stargate)}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              {getHealthLabel(stargate.health_status)}
                            </Typography>
                            <HeartbeatECG
                              signal={
                                activityMap[stargate.serial_number] ??
                                activityMap[stargate.ip_address] ??
                                stargate.last_seen
                              }
                              active={isDeviceOnline(stargate.health_status)}
                              title="Pulses on live activity from this Stargate"
                              style={{ marginLeft: 'auto' }}
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            Model: {stargate.model || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            S/N: {stargate.serial_number}
                          </Typography>
                          {stargate.ip_address && (
                            <Typography variant="body2" color="text.secondary">
                              IP: {stargate.ip_address}
                            </Typography>
                          )}
                          <StargateVersionInfo
                            ipAddress={stargate.ip_address}
                            apiKey={stargate.api_key}
                            healthStatus={stargate.health_status}
                          />
                        </CardContent>

                        {isAdmin() && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              mt: 1,
                            }}
                          >
                            <Tooltip title="Resync MQTT Client">
                              <IconButton
                                size="small"
                                disabled={resyncingId === stargate.id}
                                onClick={(e) => handleResyncClick(stargate, e)}
                              >
                                <SyncIcon
                                  fontSize="small"
                                  sx={
                                    resyncingId === stargate.id
                                      ? {
                                          animation: 'spin 1s linear infinite',
                                          '@keyframes spin': {
                                            '100%': {
                                              transform: 'rotate(360deg)',
                                            },
                                          },
                                        }
                                      : undefined
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Stargate">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(stargate);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Stargate">
                              <IconButton
                                size="small"
                                onClick={(e) => handleDeleteClick(stargate, e)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {visibleEdgeDevices.length > 0 && (
              <Box sx={{ mt: stargates.length > 0 ? 4 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DeveloperBoardIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="h5" component="h2" sx={{ color: 'text.primary' }}>
                    Edge Devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    round-trip latency probes
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {visibleEdgeDevices.map((d) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`edge-${d.id}`}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <DeveloperBoardIcon
                              sx={{
                                fontSize: 36,
                                mr: 1,
                                color: 'text.secondary',
                              }}
                            />
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {d.friendly_name || `Edge-${d.id}`}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              gap: 0.5,
                            }}
                          >
                            <span
                              className={`health-indicator ${getHealthIndicatorClass(d.health_status)}`}
                            ></span>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              {getHealthLabel(d.health_status)}
                            </Typography>
                            <HeartbeatECG
                              signal={d.last_seen}
                              active={d.last_rtt_ms != null && isDeviceOnline(d.health_status)}
                              title="Pulses when a successful latency probe is observed (30s poll)"
                              style={{ marginLeft: 'auto' }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            WG IP: {d.wg_ip || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Probe: {(d.probe_type || 'icmp').toUpperCase()}
                            {d.tcp_port ? ` :${d.tcp_port}` : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Latency:{' '}
                            {d.last_rtt_ms == null ? '—' : `${Number(d.last_rtt_ms).toFixed(1)} ms`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Duplicate Field Error Dialog */}
      <Dialog
        open={duplicateFieldDialog.open}
        onClose={() => setDuplicateFieldDialog({ open: false, field: '', value: '' })}
      >
        <DialogTitle>Duplicate {duplicateFieldDialog.field}</DialogTitle>
        <DialogContent>
          <Typography color="error">
            A Stargate with this {duplicateFieldDialog.field.toLowerCase()}{' '}
            {duplicateFieldDialog.value ? `("${duplicateFieldDialog.value}") ` : ''}
            already exists. Please use a unique {duplicateFieldDialog.field.toLowerCase()}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateFieldDialog({ open: false, field: '', value: '' })}
            color="primary"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Stargate Dialog */}
      <Dialog open={openDialog} maxWidth="sm" fullWidth disableEscapeKeyDown disableBackdropClick>
        <DialogTitle>{editingStargate ? 'Edit Stargate' : 'Add Stargate'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="normal"
            label="Friendly Name"
            name="friendly_name"
            value={stargateData.friendly_name}
            onChange={handleInputChange}
            fullWidth
            required
          />
          <TextField
            margin="normal"
            label="Model"
            name="model"
            value={stargateData.model}
            onChange={handleInputChange}
            fullWidth
            required
          />
          <TextField
            margin="normal"
            label="Serial Number"
            name="serial_number"
            value={stargateData.serial_number}
            onChange={handleInputChange}
            fullWidth
            required
          />
          <TextField
            margin="normal"
            label="IP Address"
            name="ip_address"
            value={stargateData.ip_address}
            onChange={handleInputChange}
            fullWidth
            required
            inputProps={{
              autoComplete: 'off',
              style: {
                backgroundColor: 'rgba(255, 255, 255, 0.09)',
                color: 'inherit',
                WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset',
                transition: 'background-color 5000s ease-in-out 0s',
              },
            }}
            sx={{
              '& input': {
                backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                color: 'inherit !important',
                WebkitAppearance: 'none !important',
                '&:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  WebkitTextFillColor: 'inherit !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                  borderRadius: 'inherit !important',
                  color: 'inherit !important',
                  filter: 'none !important',
                },
                '&:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                },
                '&:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                },
                '&:-webkit-autofill:active': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                },
                '&:-webkit-autofill:selected': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                },
              },
              '& .MuiInputBase-input': {
                backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                color: 'inherit !important',
                '&:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  WebkitTextFillColor: 'inherit !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                  borderRadius: 'inherit !important',
                  filter: 'none !important',
                },
              },
              '& .MuiOutlinedInput-input': {
                backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
                '&:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.09) inset !important',
                  transition: 'background-color 5000s ease-in-out 0s !important',
                },
              },
            }}
          />
          <TextField
            margin="normal"
            label="API Key"
            name="api_key"
            value={stargateData.api_key}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveStargate}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={deleting ? undefined : () => setDeleteConfirmOpen(false)}
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>Delete Stargate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the stargate "{stargateToDelete?.friendly_name}"?
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStargate}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              minWidth: 100,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(211, 47, 47, 0.5)',
                color: 'rgba(255, 255, 255, 0.8)',
              },
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MQTT Resync Confirmation Dialog */}
      <Dialog open={resyncConfirmOpen} onClose={() => setResyncConfirmOpen(false)}>
        <DialogTitle>Resync MQTT Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will push a new MQTT configuration to{' '}
            <strong>{stargateToResync?.friendly_name}</strong> ({stargateToResync?.ip_address}) and
            restart its MQTT client.
            <br />
            <br />
            The device will briefly disconnect while the MQTT client restarts with updated
            credentials and topic paths.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResyncConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleResyncMqtt} variant="contained" color="warning">
            Resync
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={60000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const StargatesPage = () => <StargatesPageInner />;

export default StargatesPage;
